/**
 * Global WebSocket Manager
 * Ensures only ONE WebSocket connection exists across the entire app
 */
import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';
import { logWebSocketDebug, isWebSocketDebugEnabled } from '../utils/websocketDebug';
import { authService } from './authService';

type AlertCallback = (alert: any) => void;
type UpdateCallback = (data: any) => void;
type ConnectCallback = (socket: Socket) => void;

interface WebSocketManager {
  socket: Socket | null;
  isConnected: boolean;
  isAuthenticated: boolean;
  subscribedDevices: Set<string>;
  alertCallbacks: Set<AlertCallback>;
  updateCallbacks: Map<string, UpdateCallback>; // keyed by subscriber ID
  connectCallbacks: Set<ConnectCallback>;
  heartbeatInterval: NodeJS.Timeout | null;
}

// Global singleton state
const manager: WebSocketManager = {
  socket: null,
  isConnected: false,
  isAuthenticated: false,
  subscribedDevices: new Set(),
  alertCallbacks: new Set(),
  updateCallbacks: new Map(),
  connectCallbacks: new Set(),
  heartbeatInterval: null
};

// Get access token from storage
function getAccessToken(): string | null {
  let accessToken = sessionStorage.getItem('access_token');
  if (!accessToken) {
    const ultronTokens = sessionStorage.getItem('ultron_tokens');
    if (ultronTokens) {
      try {
        const tokens = JSON.parse(ultronTokens);
        accessToken = tokens.accessToken;
      } catch (e) {
        logger.error('❌ [WSManager] Failed to parse ultron_tokens:', e);
      }
    }
  }
  
  // Migrate from localStorage if exists
  if (!accessToken) {
    const oldTokens = localStorage.getItem('ultron_tokens');
    if (oldTokens) {
      try {
        const session = JSON.parse(oldTokens);
        sessionStorage.setItem('ultron_tokens', oldTokens);
        localStorage.removeItem('ultron_tokens');
        accessToken = session.accessToken;
      } catch (e) {
        logger.error('❌ [WSManager] Error migrating tokens:', e);
      }
    }
  }
  
  return accessToken;
}

function getAppId(): string {
  // 🆕 For OAuth mode, always use KLMGeDMy (Unified Boards appId)
  const authMode = sessionStorage.getItem('auth_mode');
  if (authMode === 'oauth') {
    return 'KLMGeDMy';
  }

  // For API Key mode, check environment variable
  const envAppId = process.env.REACT_APP_ULTRON_APP_ID;
  if (envAppId && envAppId !== 'your-app-id-here') return envAppId;

  // Try stored credentials
  const credentials = localStorage.getItem('ultron-energy-credentials');
  if (credentials) {
    try {
      const parsed = JSON.parse(credentials);
      if (parsed.appId && parsed.appId !== 'your-app-id-here') return parsed.appId;
    } catch (e) {
      logger.error('❌ [WSManager] Failed to parse credentials:', e);
    }
  }

  return 'KLMGeDMy';
}

/**
 * Get WebSocket URL based on OAuth environment
 * Returns staging URL if user is in staging environment, otherwise production
 */
function getWebSocketUrl(): string {
  const authMode = sessionStorage.getItem('auth_mode');
  if (authMode === 'oauth') {
    try {
      // Import mcpOAuthService dynamically to avoid circular dependency
      const { mcpOAuthService } = require('./mcpOAuthService');
      const session = mcpOAuthService.getSession();
      
      // 🆕 Priority 1: Use apiUrl from session if provided by backend
      if (session?.apiUrl) {
        logger.log('🔧 [WSManager] Using backend-provided WebSocket URL:', session.apiUrl);
        return session.apiUrl;
      }
      
      // Priority 2: Use environment to determine URL
      if (session?.environment === 'staging') {
        const stagingUrl = process.env.REACT_APP_TURING_STAGING_URL || 'https://staging-turing.ultronsmart.com';
        logger.log('🔧 [WSManager] Using staging WebSocket URL:', stagingUrl);
        return stagingUrl;
      }
    } catch (e) {
      logger.warn('⚠️ [WSManager] Failed to get OAuth session for WebSocket URL:', e);
    }
  }
  
  // Default to production
  const productionUrl = 'https://turing.ultronsmart.com';
  logger.log('🔧 [WSManager] Using production WebSocket URL:', productionUrl);
  return productionUrl;
}

function startHeartbeat() {
  if (manager.heartbeatInterval) {
    clearInterval(manager.heartbeatInterval);
  }

  manager.heartbeatInterval = setInterval(() => {
    if (manager.socket?.connected) {
      manager.socket.emit('ping');
    }
  }, 15000);
}

function stopHeartbeat() {
  if (manager.heartbeatInterval) {
    clearInterval(manager.heartbeatInterval);
    manager.heartbeatInterval = null;
  }
}

/**
 * Connect to WebSocket server (if not already connected)
 * ✅ Now checks token validity and refreshes if needed before connecting
 */
export async function connect(): Promise<Socket | null> {
  // Already connected
  if (manager.socket?.connected) {
    return manager.socket;
  }

  // Already connecting
  if (manager.socket) {
    return new Promise((resolve) => {
      manager.socket!.once('connect', () => resolve(manager.socket));
    });
  }

  // ✅ Check if token is valid, refresh if needed
  if (!authService.isAuthenticated()) {
    console.log('🔄 [WSManager] Token expired, attempting refresh...');
    const refreshed = await authService.refreshToken();
    if (!refreshed) {
      logger.error('❌ [WSManager] Token refresh failed');
      return null;
    }
    console.log('✅ [WSManager] Token refreshed successfully');
  }

  const accessToken = getAccessToken();
  if (!accessToken) {
    logger.error('❌ [WSManager] No access token');
    return null;
  }

  const appId = getAppId();
  const wsUrl = getWebSocketUrl();
  console.log('🔌 [WSManager] Connecting to:', wsUrl);

  return new Promise((resolve) => {

    const socket = io(wsUrl, {
      path: '/ws',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: false, // IMPORTANT: Reuse existing connection
      query: { appId }
    });

    manager.socket = socket;

    socket.on('connect', () => {
      console.log('✅ [WSManager] Connected');
      manager.isConnected = true;
      startHeartbeat();

      socket.emit('authenticate', { accessToken, appId });
    });

    socket.on('authenticated', (data: any) => {
      if (data.success || data === true) {
        console.log('✅ [WSManager] Authenticated');
        manager.isAuthenticated = true;
        
        // Notify all connect callbacks
        manager.connectCallbacks.forEach(cb => cb(socket));
        
        // Re-subscribe to all devices
        if (manager.subscribedDevices.size > 0) {
          const devices = Array.from(manager.subscribedDevices);
          console.log(`📡 [WSManager] Subscribing to ${devices.length} devices`);
          socket.emit('subscribe-devices', { devices });
        }
        
        resolve(socket);
      } else {
        logger.error('❌ [WSManager] Auth failed:', data);
        resolve(null);
      }
    });

    socket.on('subscribe-devices-result', (data: any) => {
      if (data.success) {
        console.log(`✅ [WSManager] Subscribed to ${data.devices?.length || 0} devices`);
      }
    });

    // Listen for device updates
    socket.on('device:update', (update: any) => {
      const sn = update.data?.deviceSN || update.data?.baseSN || '?';
      const stateKeys = update.data?.delta 
        ? Object.keys(update.data.delta).slice(0, 5) 
        : update.data?.iotState 
          ? Object.keys(update.data.iotState).slice(0, 5) 
          : [];
      console.log(`📡 [WS] device:update ${sn}`, stateKeys);
      
      // Enhanced debug logging
      if (isWebSocketDebugEnabled()) {
        logWebSocketDebug('device:update', update, { source: 'websocketManager' });
      }
      
      manager.updateCallbacks.forEach(cb => cb(update.data));
    });

    socket.on('webhook:event', (payload: any) => {
      // Enhanced debug logging for raw webhook event
      if (isWebSocketDebugEnabled()) {
        logWebSocketDebug('webhook:event (raw)', payload, { 
          source: 'websocketManager',
          isArray: Array.isArray(payload),
          count: Array.isArray(payload) ? payload.length : 1
        });
      }
      
      const events = Array.isArray(payload) ? payload : [payload];
      events.forEach((event: any) => {
        const sn = event.data?.deviceSN || event.data?.baseSN || event.device || '?';
        const iotState = event.data?.delta || event.data?.iotState;
        const stateKeys = iotState ? Object.keys(iotState).slice(0, 5) : [];
        console.log(`📡 [WS] webhook:event ${sn}`, stateKeys);
        
        if (event.data) {
          manager.updateCallbacks.forEach(cb => cb(event.data));
        }
      });
    });

    // Listen for alerts
    const handleAlert = (alert: any) => {
      // Only log if it's a meaningful alert
      if (alert?.alertName) {
        console.log('🚨 [WS]', alert.alertName);
      }
      manager.alertCallbacks.forEach(cb => cb(alert));
    };

    socket.on('alerts', handleAlert);
    socket.on('climate-alert', handleAlert);
    socket.on('notification', (n: any) => n && handleAlert(n));
    socket.on('battery-alert', handleAlert);

    socket.on('disconnect', (reason: string) => {
      manager.isConnected = false;
      manager.isAuthenticated = false;
      stopHeartbeat();
      
      if (reason !== 'io client disconnect') {
        console.warn(`⚠️ [WSManager] Disconnected: ${reason}`);
      }
    });

    socket.on('connect_error', (error: any) => {
      logger.error('❌ [WSManager] Connect error:', error.message);
    });

    socket.io.on('reconnect', () => {
      console.log('✅ [WSManager] Reconnected');
    });
  });
}

/**
 * Disconnect from WebSocket server
 */
export function disconnect() {
  if (manager.socket) {
    console.log('🔌 [WSManager] Disconnecting...');
    stopHeartbeat();
    manager.socket.disconnect();
    manager.socket.removeAllListeners();
    manager.socket = null;
    manager.isConnected = false;
    manager.isAuthenticated = false;
    manager.subscribedDevices.clear();
  }
}

// Track if we've already subscribed to avoid duplicates
let hasSubscribed = false;

/**
 * Subscribe to devices (only subscribes ONCE)
 */
export function subscribeDevices(devices: string[]) {
  // Only subscribe once
  if (hasSubscribed) return;
  
  devices.forEach(d => manager.subscribedDevices.add(d));
  
  if (manager.socket?.connected && manager.isAuthenticated) {
    const allDevices = Array.from(manager.subscribedDevices);
    console.log(`📡 [WSManager] Subscribing to ${allDevices.length} devices`);
    manager.socket.emit('subscribe-devices', { devices: allDevices });
    hasSubscribed = true;
  }
}

/**
 * Register an alert callback
 */
export function onAlert(callback: AlertCallback): () => void {
  manager.alertCallbacks.add(callback);
  return () => manager.alertCallbacks.delete(callback);
}

/**
 * Register an update callback
 */
export function onUpdate(subscriberId: string, callback: UpdateCallback): () => void {
  manager.updateCallbacks.set(subscriberId, callback);
  return () => manager.updateCallbacks.delete(subscriberId);
}

/**
 * Register a connect callback
 */
export function onConnect(callback: ConnectCallback): () => void {
  manager.connectCallbacks.add(callback);
  // If already connected, call immediately
  if (manager.socket?.connected && manager.isAuthenticated) {
    callback(manager.socket);
  }
  return () => manager.connectCallbacks.delete(callback);
}

/**
 * Get current connection state
 */
export function getState() {
  return {
    isConnected: manager.isConnected,
    isAuthenticated: manager.isAuthenticated,
    subscribedDevices: Array.from(manager.subscribedDevices),
    socket: manager.socket
  };
}

export const websocketManager = {
  connect,
  disconnect,
  subscribeDevices,
  onAlert,
  onUpdate,
  onConnect,
  getState
};

// 🆕 Debug: Mount to window for console debugging
if (typeof window !== 'undefined') {
  (window as any).websocketManager = websocketManager;
  (window as any)._wsManager = manager; // Internal state for debugging
}

export default websocketManager;

